import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';

/**
 * Staff "What's New" button + popup, per portal. On mount it loads the active
 * announcements for its portal and AUTO-POPS once if any are unseen; dismissing
 * marks them seen (server-side) so it won't auto-pop again. The button re-opens
 * the list anytime, badging how many are still unseen and tagging each "New".
 * Self-contained (inline styles) so it drops into any portal header.
 */
export interface Announcement {
  id: number;
  title: string;
  body: string;
  target_portal: string;
  published_at: string | null;
  created_at: string;
  seen: boolean;
}

interface Props {
  portal: 'ADMIN' | 'POS' | 'MILL';
}

const WhatsNew: React.FC<Props> = ({ portal }) => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/announcements/?portal=${portal}`);
      const list: Announcement[] = res.data.announcements || [];
      setAnnouncements(list);
      if (list.some((a) => !a.seen)) setOpen(true); // auto-pop once
    } catch {
      /* non-fatal */
    }
  }, [portal]);

  useEffect(() => { load(); }, [load]);

  const unseenCount = announcements.filter((a) => !a.seen).length;

  const close = async () => {
    setOpen(false);
    const unseen = announcements.filter((a) => !a.seen);
    if (unseen.length === 0) return;
    await Promise.all(unseen.map((a) => api.post(`/announcements/${a.id}/seen/`).catch(() => null)));
    setAnnouncements((prev) => prev.map((a) => ({ ...a, seen: true })));
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="What's New"
        style={{
          position: 'relative', background: 'transparent', border: '1px solid currentColor',
          borderRadius: 6, padding: '4px 10px', color: 'inherit', cursor: 'pointer',
          font: 'inherit', fontSize: 13, fontWeight: 600,
        }}
      >
        What&apos;s New
        {unseenCount > 0 && (
          <span
            data-testid="whats-new-badge"
            style={{
              position: 'absolute', top: -6, right: -6, minWidth: 16, height: 16,
              padding: '0 4px', borderRadius: 8, background: '#dc2626', color: '#fff',
              fontSize: 10, lineHeight: '16px', textAlign: 'center', fontWeight: 700,
            }}
          >
            {unseenCount}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          data-testid="whats-new-overlay"
          onClick={close}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4000,
          }}
        >
          <div
            data-testid="whats-new-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', color: '#1f2937', borderRadius: 10, width: '92%', maxWidth: 520, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: 0 }}>What&apos;s New</h3>
              <button onClick={close} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280' }}>&times;</button>
            </div>
            <div style={{ padding: 18, overflowY: 'auto' }}>
              {announcements.length === 0 ? (
                <p style={{ color: '#6b7280' }}>No announcements right now.</p>
              ) : (
                announcements.map((a) => (
                  <div key={a.id} style={{ marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <strong style={{ fontSize: 15 }}>{a.title}</strong>
                      {!a.seen && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#166534', background: '#DCFCE7', borderRadius: 3, padding: '1px 6px' }}>NEW</span>
                      )}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, marginTop: 4 }}>{a.body}</div>
                    {a.published_at && (
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                        {new Date(a.published_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            <div style={{ padding: '12px 18px', borderTop: '1px solid #e5e7eb', textAlign: 'right' }}>
              <button onClick={close} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#1a4d1a', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                Got it
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default WhatsNew;
